// Life & Wealth Solutions V24 - Agent 12 Dashboard
// Session 8 - Added Demo Mode Support
// The "Agent 12" interface for reviewing AI agent work and providing licensed authority

import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import WhaleOpportunities from '../components/agent12/WhaleOpportunities';
import PendingApprovals from '../components/agent12/PendingApprovals';
import ActivitySummary from '../components/agent12/ActivitySummary';
import ComplianceShield from '../components/agent12/ComplianceShield';
import SystemIntegrityWidget from '../components/agent12/SystemIntegrityWidget';
import { isDemoMode, mockData, setDemoMode } from '../services/mockDataService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface DashboardStats {
  pending_approvals: number;
  whale_opportunities: number;
  policies_bound_today: number;
  total_commission_today: number;
  agent_11_interventions: number;
  agent_9_escalations: number;
}

const Agent12Dashboard: React.FC = () => {
  const { user, isAgent12, isAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'whales' | 'approvals' | 'activity'>('whales');
  const [demoMode, setDemoModeState] = useState(isDemoMode());

  useEffect(() => {
    loadDashboardData();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [demoMode]);

  const loadDashboardData = async () => {
    try {
      // Check if demo mode is enabled
      if (isDemoMode()) {
        // Use mock data
        const mockStats = mockData.dashboardStats();
        setStats(mockStats);
        setError('');
        setLoading(false);
        return;
      }

      // Production mode - call actual API
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/agent12/dashboard/${user?.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      setError('');
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!isAgent12 && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🛡️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Agent 12 Access Required</h1>
          <p className="text-gray-600">Only Agent 12 (HITL) or Admin can access this dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Agent 12 Command Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <span className="text-4xl">👤</span>
                Agent 12 Command Center
              </h1>
              <p className="mt-1 text-blue-100">
                Human-in-the-Loop Authority • Licensed Agent Dashboard
              </p>
              <p className="mt-1 text-sm text-blue-200">
                Welcome, {user?.full_name}
              </p>
            </div>
            <div className="text-right flex flex-col items-end gap-3">
              <div>
                <div className="text-sm text-blue-200">Your Role</div>
                <div className="text-2xl font-bold">{user?.role.toUpperCase()}</div>
              </div>
              {/* Demo Mode Toggle */}
              <button
                onClick={() => {
                  const newMode = !demoMode;
                  setDemoMode(newMode);
                  setDemoModeState(newMode);
                  window.location.reload(); // Reload to apply demo mode
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  demoMode
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
              >
                {demoMode ? '🎭 Demo Mode: ON' : '🎭 Demo Mode: OFF'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Whale Opportunities */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Whale Opportunities</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats?.whale_opportunities || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">High-value policies ready</p>
              </div>
              <div className="text-5xl">🐋</div>
            </div>
          </div>

          {/* Pending Approvals */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats?.pending_approvals || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Awaiting your signature</p>
              </div>
              <div className="text-5xl">⏳</div>
            </div>
          </div>

          {/* Policies Bound Today */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Bound Today</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats?.policies_bound_today || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ${stats?.total_commission_today?.toLocaleString() || 0} commission
                </p>
              </div>
              <div className="text-5xl">✅</div>
            </div>
          </div>

          {/* My Sentinel Interventions */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">My Sentinel Flags</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats?.agent_11_interventions || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Compliance alerts today</p>
              </div>
              <div className="text-5xl">🛡️</div>
            </div>
          </div>

          {/* My Maestro Escalations */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">My Maestro Escalations</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats?.agent_9_escalations || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Orchestrator handoffs</p>
              </div>
              <div className="text-5xl">🎯</div>
            </div>
          </div>

          {/* Efficiency Score */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Efficiency Score</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">98.7%</p>
                <p className="text-xs text-gray-500 mt-1">AI automation rate</p>
              </div>
              <div className="text-5xl">⚡</div>
            </div>
          </div>
        </div>

        {/* System Integrity Widget - Autonomic Self-Healing */}
        <div className="mb-8">
          <SystemIntegrityWidget />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('whales')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'whales'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🐋 Whale Opportunities ({stats?.whale_opportunities || 0})
              </button>
              <button
                onClick={() => setActiveTab('approvals')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'approvals'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⏳ Pending Approvals ({stats?.pending_approvals || 0})
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'activity'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Activity Summary
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'whales' && <WhaleOpportunities userId={user?.user_id || ''} />}
            {activeTab === 'approvals' && <PendingApprovals userId={user?.user_id || ''} />}
            {activeTab === 'activity' && <ActivitySummary userId={user?.user_id || ''} />}
          </div>
        </div>

        {/* Compliance Shield Info */}
        <ComplianceShield />
      </div>
    </div>
  );
};

export default Agent12Dashboard;
